import { ref, computed, onMounted, onBeforeUnmount, toValue } from 'vue'
import { type MaybeRefOrGetter } from 'vue'

import { hashString64 } from '../utils/strings'

import type { ComposedMetaData } from '@xcel-next/composer-schema'

const PLACEMENT_MAPPING: Record<string, string> = {
  autocomparison: 'comparison_table',
  single: 'shopping_widget',
  double: 'shopping_widget_double',
  triple: 'shopping_widget_triple',
  slider: 'shopping_widget_slider',
  affiliateLink: 'affiliate_link',
  carOfferBuyOrLease: 'teaser',
  carOfferFind: 'teaser',
  carOfferLease: 'teaser',
  carOfferBuy: 'teaser'
}

const PRODUCT_LABEL_SELECTOR = '.bx-product__label:not(.bx-product__label--shopping-days, .bx-product__label--no)'
const SHOPPING_DAYS_LABEL_SELECTOR = '.bx-product__label--shopping-days:not(.bx-product__label--no)'

export function useProductCardTracking (metaData: MaybeRefOrGetter<ComposedMetaData | undefined>) {
  const observer = ref<IntersectionObserver | null>(null)
  const trackedVisibility = ref<Set<string> | null>(null)
  const cocoCards = ref<HTMLElement[]>([])
  const cocoGroups = ref<HTMLElement[]>([])
  const otherPartners = ref<HTMLAnchorElement[]>([])
  const inlineAffiliateLinks = ref<HTMLAnchorElement[]>([])

  const enabledProductCardTracking = computed(() => {
    const meta = toValue(metaData)
    return meta?.affiliateContent || meta?.cocoContent || meta?.showAffiliateDisclaimer
  })

  onMounted(() => {
    if (enabledProductCardTracking.value) {
      trackedVisibility.value = new Set()
      setupTracking()
    }
  })

  onBeforeUnmount(() => {
    observer.value?.disconnect()

    cocoCards.value.forEach((cocoCard) => {
      cocoCard.removeEventListener('click', handleCocoCardClick)
    })
    otherPartners.value.forEach((otherPartner) => {
      otherPartner.removeEventListener('click', handleOtherPartnerClick)
    })
    inlineAffiliateLinks.value.forEach((inlineAffiliateLink) => {
      inlineAffiliateLink.removeEventListener('click', handleInlineAffiliateLinkClick)
    })
  })

  /**
   * while we not have unique tracking IDs to identify products,
   * we need to extract the product ID from the URL or use a default ID
   * the default ID is a generated hash value based on the product's url
   */
  function extractProductId (url: string, defaultId: string): string {
    try {
      const parsedUrl = new URL(url)

      // 1. Amazon-URL with ASIN i.e. https://www.amazon.de/dp/B0DB8D5VGT
      const amazonAsinMatch = parsedUrl.pathname.match(/\/dp\/([A-Z0-9]{10})/)
      if (amazonAsinMatch) return amazonAsinMatch[1]

      // 2. Awin-URL with awinmid i.e. https://www.awin1.com/cread.php?awinmid=10092
      if (parsedUrl.hostname.includes('awin1.com')) {
        const awinmid = parsedUrl.searchParams.get('awinmid')
        if (awinmid) return awinmid
      }

      // 3. no matching pattern >> return defaultId
      return defaultId
    } catch {
      return defaultId
    }
  }

  function calcRelativePosition (entry: Element): string {
    const entryBound = entry.getBoundingClientRect()
    const entryTopDistance = entryBound.top + window.scrollY
    const assetContainer = document.querySelector<HTMLElement>('main > section > div > .bx-paragraph-list')
    if (!assetContainer) return '0%'
    const assetContainerBound = assetContainer.getBoundingClientRect()
    const assetBottomDistance = assetContainerBound.height + assetContainerBound.top + window.scrollY
    return Math.round(100 / assetBottomDistance * entryTopDistance) + '%'
  }

  function setupTracking () {
    cocoCards.value = Array.from(document.querySelectorAll<HTMLElement>('.bx-product, [data-ga-coco-group-type="affiliateLink"], .bx-carwow'))
    if (!cocoCards.value.length) return

    cocoGroups.value = Array.from(document.querySelectorAll<HTMLElement>('.bx-product-group, .bx-affiliate-link, .bx-carwow'))
    cocoGroups.value.forEach((cocoGroup, index) => {
      cocoGroup.dataset.gaCocoPosition = `${index + 1}/${cocoGroups.value.length}`
    })

    observer.value = new IntersectionObserver(visibleObserverCallback, {
      root: null,
      rootMargin: '0px',
      threshold: 0.5
    })

    cocoCards.value.forEach((cocoCard) => {
      observer.value!.observe(cocoCard)
      if (cocoCard.classList.contains('bx-carwow')) {
        cocoCard.querySelectorAll('a, button').forEach((clickTarget) => {
          clickTarget.addEventListener('click', handleCarOfferWidgetClick)
        })
      } else {
        cocoCard.addEventListener('click', handleCocoCardClick)
      }
    })

    otherPartners.value = Array.from(document.querySelectorAll<HTMLAnchorElement>('.bx-product__other-partners a'))
    otherPartners.value.forEach((otherPartner) => {
      otherPartner.addEventListener('click', handleOtherPartnerClick)
    })

    inlineAffiliateLinks.value = Array.from(document.querySelectorAll<HTMLAnchorElement>('.bx-content__continuous-text a[target="_blank"][rel="sponsored"]'))
    inlineAffiliateLinks.value.forEach((inlineAffiliateLink) => {
      inlineAffiliateLink.addEventListener('click', handleInlineAffiliateLinkClick)
    })
  }

  function handleInlineAffiliateLinkClick (event: Event) {
    const link = event.currentTarget
    if (!(link instanceof HTMLAnchorElement)) return
    const url = link.href
    const nonMerchants = ['awin1']
    let extractedMerchant = 'unknown_merchant'

    try {
      const parsedUrl = new URL(url)
      if (nonMerchants.includes(parsedUrl.hostname.split('.')[1])) {
        const ued = parsedUrl.searchParams.get('ued')
        if (ued) {
          extractedMerchant = new URL(ued).hostname.replace(/^www\./, '').split('.')[0]
        }
      } else {
        extractedMerchant = parsedUrl.hostname.replace(/^www\./, '').split('.')[0]
      }
    } catch {
      // malformed URL, keep default
    }

    pushTrackingData({
      event: 'product_text_link_click',
      product_url: url,
      merchant: extractedMerchant,
      product_card_rel_position: calcRelativePosition(link),
      product_card_id: extractProductId(url, hashString64(url))
    })
  }

  function handleCarOfferWidgetClick (event: Event) {
    const target = event.target
    if (!(target instanceof Element)) return
    const widgetWrapper = target.closest<HTMLElement>('.bx-carwow')
    if (!widgetWrapper) return
    handleCarOfferWidgetTracking(widgetWrapper, 'click', target.closest('a, button'))
  }

  // We want to track the existence of optional elements on product cards impressions or clicks and return corresponding pipe separated strings for the data layer object
  function aggregateProductCardElements (target: HTMLElement, excludeProductGroupLabel = false): string {
    const relatedProductGroup = target?.closest('.bx-product-group')
    const dataProductUniqueIdSelector = `[data-product-unique-id="${target.dataset.gaCocoUniqueId ?? ''}"]`
    const productGroupLabelTrackingStr = !excludeProductGroupLabel && target?.querySelector('.bx-product-group__label') ? 'productGroupLabel' : false
    const productLabelTrackingStr = target?.querySelector(PRODUCT_LABEL_SELECTOR) ? 'productLabel' : false
    const shoppingDaysLabelTrackingStr = target?.querySelector(SHOPPING_DAYS_LABEL_SELECTOR) ? 'shoppingDaysLabel' : false
    const otherPartnersTrackingStr = relatedProductGroup?.querySelector(`.bx-product__other-partners${dataProductUniqueIdSelector}`) ? 'otherPartners' : false
    const editorialStarRatingTrackingStr = relatedProductGroup?.querySelector(`.bx-product__rating${dataProductUniqueIdSelector}:not(.bx-product__rating--no)`) ? 'rating' : false
    const prosConsTrackingStr = relatedProductGroup?.querySelectorAll(`#pros-and-cons ul li${dataProductUniqueIdSelector}`)?.length ? 'prosCons' : false
    const relatedTechnicalDataWrapper = relatedProductGroup?.querySelector(`#technical-data div${dataProductUniqueIdSelector}`)
    const technicalDataTrackingStr = relatedTechnicalDataWrapper && relatedTechnicalDataWrapper?.textContent !== '-' ? 'tech' : false
    return [
      productGroupLabelTrackingStr,
      productLabelTrackingStr,
      shoppingDaysLabelTrackingStr,
      otherPartnersTrackingStr,
      editorialStarRatingTrackingStr,
      prosConsTrackingStr,
      technicalDataTrackingStr
    ].filter(Boolean).join('|').replace(/^$/, 'none')
  }

  function handleCocoCardClick (event: Event) {
    const clickedElement = event.target
    if (!(clickedElement instanceof Element)) return
    const closestLink = clickedElement.closest<HTMLAnchorElement>('.bx-product, a')
    if (!closestLink) return
    const clickedLabel = clickedElement.closest(PRODUCT_LABEL_SELECTOR) ? 'productLabel' : false
    const clickedShoppingDaysLabel = clickedElement.closest(SHOPPING_DAYS_LABEL_SELECTOR) ? 'shoppingDaysLabel' : false
    const clickedImage = clickedElement.tagName === 'IMG' ? 'image' : false
    const clickedTitle = clickedElement.classList.contains('bx-product__title') ? 'title' : false
    const additionalData = {
      affiliate_link_position: clickedLabel || clickedShoppingDaysLabel || clickedImage || clickedTitle || 1,
      elements: aggregateProductCardElements(closestLink, true)
    }
    enrichTrackingData(closestLink, 'click', additionalData)
  }

  function handleOtherPartnerClick (event: Event) {
    const eventTarget = event.target
    if (!(eventTarget instanceof Element)) return
    const closestWrapper = eventTarget.closest<HTMLElement>('.bx-product__other-partners')
    if (!closestWrapper) return
    const productUniqueId = closestWrapper.dataset.productUniqueId
    const closestProductGroup = eventTarget.closest<HTMLElement>('.bx-product-group')
    if (!closestProductGroup || !productUniqueId) return
    const relatedProduct = closestProductGroup.querySelector<HTMLAnchorElement>(`a[data-ga-coco-unique-id="${productUniqueId}"]`)
    const closestLink = eventTarget.closest<HTMLAnchorElement>('a')
    if (!relatedProduct || !closestLink) return
    const partnerPosition = Number(closestLink.dataset.gaCocoPartnerPosition)
    const additionalData = {
      merchant: closestLink.querySelector('span')?.textContent || '',
      affiliate_link_position: Number.isFinite(partnerPosition) ? partnerPosition + 1 : '',
      product_url: closestLink.href,
      elements: aggregateProductCardElements(relatedProduct, true)
    }
    enrichTrackingData(relatedProduct, 'click', additionalData)
  }

  function visibleObserverCallback (entries: IntersectionObserverEntry[]) {
    entries.forEach(entry => {
      if (!(entry.target instanceof HTMLElement) || !trackedVisibility.value) return
      const target = entry.target
      const uniqueProductInGroupId = target.dataset.gaCocoUniqueId + '-' + target.dataset.gaCocoGroupType
      if (entry.isIntersecting && !trackedVisibility.value.has(uniqueProductInGroupId)) {
        trackedVisibility.value.add(uniqueProductInGroupId)
        if (entry.target.classList.contains('bx-carwow')) {
          handleCarOfferWidgetTracking(target, 'visibility', target.querySelector('a, button'))
        } else {
          enrichTrackingData(target, 'visibility', { elements: aggregateProductCardElements(target) })
        }
      }
    })
  }

  function handleCarOfferWidgetTracking (widget: HTMLElement, eventName: string, linkTarget: Element | null = null) {
    const widgetType = (widget.dataset.gaCocoGroupType ?? '').replace('carOffer', '').toLowerCase()
    const productUrl = widgetType === 'find'
      ? (linkTarget instanceof HTMLElement ? linkTarget.dataset.href : undefined)
      : (linkTarget instanceof HTMLAnchorElement ? linkTarget.href : undefined)
    const productCardPosition = widget.closest('.bx-carwow')
      ? (widget.closest<HTMLElement>('.bx-carwow')?.dataset.gaCocoPosition || 'unknown_position')
      : 'unknown_position'
    const { make, model, purchaseType } = getCarOfferDetails(productUrl)
    const additionalData = {
      merchant: 'car_wow',
      affiliate_link_position: `${make}_${model}_${purchaseType}`,
      product_card_id: widgetType === 'find' ? 'find_buy_lease' : `${make}_${model}_${widgetType === 'buyorlease' ? 'buy_and_lease' : widgetType}`,
      product_card_position: productCardPosition,
      product_url: productUrl
    }
    enrichTrackingData(widget, eventName, additionalData)
  }

  function getCarOfferDetails (url: string | undefined) {
    if (url === undefined) return { make: 'unknown', model: 'unknown', purchaseType: 'unknown' }
    const pathSegments = new URL(url).pathname.split('/').filter(Boolean)
    const [make, model, thirdSegment] = pathSegments
    return { make, model, purchaseType: thirdSegment === 'leasing' ? 'lease' : 'buy' }
  }

  function enrichTrackingData (target: HTMLElement, type: string, additionalData: Record<string, unknown> = {}) {
    const groupType = target.dataset.gaCocoGroupType
    const isAffiliateLink = groupType === 'affiliateLink'
    const event = type === 'visibility' ? 'product_card_impression' : 'product_card_click'
    const placement = PLACEMENT_MAPPING[groupType ?? ''] || 'unknown_placement'
    const merchant = target.querySelector('.bx-product__roofline')?.textContent || ''
    const targetUrl = target instanceof HTMLAnchorElement ? target.href : ''
    const defaultId = target.dataset.gaCocoUniqueId ?? hashString64(targetUrl)
    const productCardPosition = target.closest(isAffiliateLink ? '.bx-affiliate-link' : '.bx-product-group')
      ? (target.closest<HTMLElement>(isAffiliateLink ? '.bx-affiliate-link' : '.bx-product-group')?.dataset.gaCocoPosition || 'unknown_position')
      : 'unknown_position'

    const data: Record<string, unknown> = {
      event,
      product_card_id: extractProductId(targetUrl, defaultId),
      placement,
      merchant,
      affiliate_link_position: 1,
      elements: 'none',
      product_card_position: productCardPosition,
      product_card_rel_position: calcRelativePosition(target),
      product_url: targetUrl,
      ...additionalData
    }

    if (isAffiliateLink) delete data.merchant
    if (event === 'product_card_impression') delete data.affiliate_link_position

    pushTrackingData(data)
  }

  function pushTrackingData (data: Record<string, unknown>) {
    window.dataLayer = window.dataLayer || []
    window.dataLayer.push(data)
  }

  return {
    enabledProductCardTracking,
    setupTracking,
    pushTrackingData,
    enrichTrackingData
  }
}
